import { z } from 'zod'

/**
 * Admin report audit event - issue #354.
 *
 * One row per admin-report interaction (view/generate/export/denied).
 * Lives in `admin_report_audit_events`, separate from user-facing
 * `activity_logs`. Written via `POST /v1/admin/report-audit` by the
 * Evidence.dev reverse proxy (slice #355) and the future admin CLI.
 *
 * NEVER include raw Gmail metadata (subject, snippet, body) in
 * `params_json` - the writer rejects payloads containing these keys.
 */

export const AdminReportAuditActionSchema = z.enum([
  'view',
  'export',
  'generate',
  'denied',
])
export type AdminReportAuditAction = z.infer<typeof AdminReportAuditActionSchema>

export const AdminReportRedactionModeSchema = z.enum([
  'aggregate',
  'redacted_examples',
  'full',
])
export type AdminReportRedactionMode = z.infer<
  typeof AdminReportRedactionModeSchema
>

/** Reject any param key that could carry email content. */
export const FORBIDDEN_PARAMS_KEYS = [
  'subject',
  'snippet',
  'body',
  'body_html',
  'body_text',
  'message_body',
  'email_subject',
  'email_snippet',
  'email_body',
] as const

export const AdminReportAuditEventRequestSchema = z.object({
  target_user_id: z.string().uuid().nullable().optional(),
  report_slug: z.string().min(1).max(200),
  params_json: z.record(z.string(), z.unknown()).default({}),
  action: AdminReportAuditActionSchema,
  redaction_mode: AdminReportRedactionModeSchema,
  request_id: z.string().min(1).max(200).optional(),
  ip: z.string().max(64).optional(),
  user_agent: z.string().max(500).optional(),
})
export type AdminReportAuditEventRequest = z.infer<
  typeof AdminReportAuditEventRequestSchema
>

export const AdminReportAuditEventResponseSchema = z.object({
  ok: z.literal(true),
  id: z.string().uuid(),
  created_at: z.string().datetime(),
})
export type AdminReportAuditEventResponse = z.infer<
  typeof AdminReportAuditEventResponseSchema
>
